import * as React from "react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { transferProductOwner } from "../../lib/api/products";
import type { Product } from "../../lib/api/products";
import { useProductTargetOrganizations } from "./useProductTargetOrganizations";

export interface TransferProductOwnerModalProps {
  open: boolean;
  setOpen: (v: boolean) => void;
  token: string | null;
  productId: string;
  productName: string;
  currentOrganizationId: string | null;
  onTransferred?: (product: Product) => void;
}

export function TransferProductOwnerModal({
  open,
  setOpen,
  token,
  productId,
  productName,
  currentOrganizationId,
  onTransferred,
}: TransferProductOwnerModalProps) {
  const { organizations, loading } = useProductTargetOrganizations(token, open);
  const options = organizations.filter((o) => o.id !== currentOrganizationId);
  const [organizationId, setOrganizationId] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setOrganizationId("");
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !organizationId) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await transferProductOwner(token, productId, organizationId);
      setOpen(false);
      onTransferred?.(updated);
    } catch (err: any) {
      setError(err?.message || "Failed to transfer product. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} minWidth="440px" maxWidth="520px" closeOnClickOff>
      <Modal.Form onSubmit={handleSubmit}>
        <Modal.Header>
          <Text level="h2" weight="bold">
            Transfer ownership
          </Text>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Text level="body-small" color="muted">
              Members of the new organization will get access to {productName} based on their
              organization role. Members of the current organization will lose inherited access.
            </Text>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className={formStyles.label}>New owning organization</label>
              <select
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                disabled={saving || loading}
                className={formStyles.input}
              >
                <option value="">— Select organization —</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
            {!loading && options.length === 0 && (
              <Callout variant="info">
                There are no other organizations you can transfer this product to.
              </Callout>
            )}
          </div>
          {error && (
            <div style={{ marginTop: 16 }}>
              <Callout variant="error">{error}</Callout>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" color="neutral" onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={saving || !organizationId}>
            {saving ? "Transferring…" : "Transfer"}
          </Button>
        </Modal.Footer>
      </Modal.Form>
    </Modal>
  );
}
